import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  Modal,
  SelectInput,
  StatusBadge,
  TextareaInput,
  useToast,
  type SelectOption,
} from '@kss/ui';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Info,
  Package,
  RefreshCw,
  RotateCcw,
  Search,
  Truck,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@kss/auth';
import {
  authService,
  orderReturnService,
  salesOrderService,
  type SalesOrder,
} from '@kss/api';

/**
 * Order tracker — dedicated page customers reach via the "Track Order"
 * footer link or the "Track order" tile on `/account`.
 *
 * Shows every order belonging to the signed-in customer plus a
 * visual 4-step timeline (Pending → Confirmed → Shipped → Delivered,
 * with Cancelled and Returned as side-branches). The page also accepts
 * `?orderNumber=KSS-…` as a deep link so customer support can paste
 * a tracking URL straight into a WhatsApp chat.
 *
 * SSOO ISSUE 1 — customer needs to *see* status transitions clearly.
 * The timeline card now surfaces the `shippedAt` timestamp under the
 * Shipped step and the `updatedAt` timestamp under Delivered, plus a
 * Cancel button for orders whose status is still Draft / Confirmed
 * (before Shipped). The customer never changes status directly — the
 * cancel action is the one exception, and it writes SalesOrderStatus=5
 * via `salesOrderService.update` (same pattern as `AccountOrders`).
 *
 * SSOO ISSUE 5 — delivered orders now expose a "Request Return"
 * action that opens a modal (reason dropdown, notes textarea, optional
 * refund method). Submitting POSTs to `/api/OrderReturn`; the service
 * side then cascades SalesOrder.Status to 6 = Returned so the tracker
 * moves the row into the "Returns & refunds" section on the next
 * fetch. Status stays a free-text string ("Requested") to match what
 * the panel's `NewOrderReturn` form sends and what the entity column
 * expects — the `status: 1` phrasing in the brief was a hint for the
 * eventual int enum, not the wire contract today.
 *
 * SSOO ROUND-2 UX pass — user complaint was "still not seeing status
 * updates" and "still no returns option". Fixes:
 *   1. Prominent "Current: X" StatusBadge in every card header so the
 *      current stage is unmissable (no more decoding the pipeline).
 *   2. "Last updated N min ago" stamp derived from `order.updatedAt`
 *      so the shopper can see when the backend last touched the row.
 *   3. Per-card "Refresh status" button — same fetch as the header
 *      one, but lives on the row the shopper is watching.
 *   4. Return button now appears on Shipped=3 too (with label
 *      "Report an issue"), not only on Delivered=4. Primary variant,
 *      so it can't disappear into border noise.
 *   5. Helper text when the customer has active orders but none in
 *      returnable states, so they know WHERE the button will surface.
 */

type Stage = 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'returned';

const STAGE_ORDER: Stage[] = ['pending', 'confirmed', 'shipped', 'delivered'];

const STAGE_LABEL: Record<Stage, string> = {
  pending: 'Order placed',
  confirmed: 'Confirmed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

const STAGE_ICON: Record<Stage, typeof Package> = {
  pending: Clock,
  confirmed: CheckCircle2,
  shipped: Truck,
  delivered: Package,
  cancelled: XCircle,
  returned: RotateCcw,
};

// SSOO ISSUE 5 — reasons match the panel's OrderReturn form for
// consistency. Backend `Reason` column is a free-text string, so we
// send the label directly.
const RETURN_REASONS: SelectOption[] = [
  { value: 'Defective', label: 'Defective / damaged' },
  { value: 'Not as described', label: 'Not as described' },
  { value: 'Wrong item', label: 'Wrong item received' },
  { value: 'Changed mind', label: 'Changed my mind' },
  { value: 'Other', label: 'Other' },
];

const REFUND_METHODS: SelectOption[] = [
  { value: '', label: 'No preference' },
  { value: 'Original payment', label: 'Refund to original payment method' },
  { value: 'Store credit', label: 'Store credit' },
  { value: 'Bank transfer', label: 'Bank transfer' },
];

function stageOf(o: SalesOrder): Stage {
  const raw = o.status;
  if (typeof raw === 'number') {
    // SSO NEW ISSUE 3 — SalesOrderStatus is a code-owned enum where
    // 6 = Returned is set by `OrderReturnService` when a return is
    // created against the sales order. Recognising it here is what
    // routes the row into the "Returns & refunds" section instead of
    // leaving it in the active timeline.
    if (raw === 6) return 'returned';
    if (raw === 5) return 'cancelled';
    if (raw === 4) return 'delivered';
    if (raw === 3) return 'shipped';
    if (raw === 2) return 'confirmed';
    return 'pending';
  }
  const v = String(raw ?? '').toLowerCase();
  if (v === 'returned') return 'returned';
  if (v === 'cancelled' || v === 'canceled') return 'cancelled';
  if (v === 'delivered') return 'delivered';
  if (v === 'shipped') return 'shipped';
  if (v === 'confirmed') return 'confirmed';
  return 'pending';
}

function isPastStage(current: Stage, candidate: Stage): boolean {
  if (current === 'cancelled') return false;
  // Returned orders shipped + delivered before the return — colour the
  // full pipeline as done so the customer sees where the flow got to.
  if (current === 'returned') return true;
  return STAGE_ORDER.indexOf(current) >= STAGE_ORDER.indexOf(candidate);
}

function isCancellable(s: Stage): boolean {
  // SSOO ISSUE 1 — cancel only before the order leaves the warehouse.
  return s === 'pending' || s === 'confirmed';
}

function isReturnable(s: Stage): boolean {
  /* SSOO ISSUE 5 (ROUND-2) — the previous fix only exposed the button
     once the panel had flipped status to Delivered=4. Users routinely
     want to raise an issue as soon as the order is Shipped=3 (item
     arrived damaged, wrong SKU picked, etc.) so we now surface the
     button in BOTH states. The label switches to "Report an issue"
     for Shipped so the wording matches the shopper's mental model — a
     shipped order isn't "delivered" from their POV yet, but a damaged
     package coming off the truck is still their problem to raise.
     Once the customer submits, backend cascades SO.Status → 6
     (Returned) and the button disappears — no double-submits. */
  return s === 'shipped' || s === 'delivered';
}

function returnActionLabel(s: Stage): string {
  // SSOO ISSUE 5 (ROUND-2) — friendlier verb for pre-delivery reports.
  return s === 'shipped' ? 'Report an issue' : 'Request return';
}

function fmtStamp(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* SSOO ISSUE 1 (ROUND-2) — human-readable "5 min ago" style stamp so
   the customer can tell at a glance how fresh the tracker view is.
   Falls back to a compact locale date once the delta exceeds a day. */
function fmtRelative(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const deltaMs = Date.now() - d.getTime();
  const s = Math.floor(deltaMs / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* SSOO ISSUE 1 (ROUND-2) — human-readable label for the "Current: X"
   badge. Reads from the same stage function the timeline uses so the
   badge and the pipeline are always in sync. */
function stageDisplayLabel(s: Stage): string {
  return STAGE_LABEL[s];
}

/**
 * SSOO ISSUE 5 — payload for the return modal. Kept as component
 * state on the tracker page so the same modal can be reused for any
 * row without a per-card local state explosion.
 */
interface ReturnDraft {
  order: SalesOrder;
  reason: string;
  notes: string;
  refundMethod: string;
}

export default function AccountOrderTracker() {
  const toast = useToast();
  const { isAuthenticated } = useAuth();
  const [params, setParams] = useSearchParams();
  const queriedOrder = (params.get('orderNumber') ?? '').trim();

  const [filter, setFilter] = useState(queriedOrder);
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);
  /* Last-refreshed timestamp so the customer can see how stale the
     tracker is. Updated on every successful fetch. Bug ref: AI Agent
     2026-06-11 — "Order tracking status not being updated correctly". */
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  // SSOO ISSUE 1 — cancel confirmation modal state.
  const [cancelling, setCancelling] = useState<SalesOrder | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  // SSOO ISSUE 5 — return-request modal state.
  const [returnDraft, setReturnDraft] = useState<ReturnDraft | null>(null);
  const [returnBusy, setReturnBusy] = useState(false);
  const [returnError, setReturnError] = useState<string | null>(null);

  // Fetch all the customer's orders. The `myOrders` endpoint is
  // JWT-customerId-scoped, so we never leak rows across customers.
  const loadOrders = useCallback(async () => {
    if (!isAuthenticated) {
      setOrders([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await authService.myOrders({ pageSize: 50 });
      const items = Array.isArray(resp?.items) ? (resp.items as SalesOrder[]) : [];
      items.sort((a, b) => {
        const da = a.orderDate ?? a.createdAt ?? '';
        const db = b.orderDate ?? b.createdAt ?? '';
        return db.localeCompare(da);
      });
      setOrders(items);
      setRefreshedAt(new Date());
    } catch (e) {
      setOrders([]);
      toast.error(
        'Could not load orders',
        e instanceof Error ? e.message : 'Try again later.',
      );
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, toast]);

  // First-mount + reauth-triggered fetch.
  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  /* Auto-refresh on tab focus + every 60 s while the tab is visible.
     Customers expect "tracking" to stay current — without this the
     page silently shows stale shipping status if the user keeps the
     tab open after a backend status flip. The interval pauses when
     the tab is hidden so we don't hammer the API in the background. */
  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => { void loadOrders(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void loadOrders();
        if (intervalRef.current == null) {
          intervalRef.current = window.setInterval(() => { void loadOrders(); }, 60_000);
        }
      } else if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    onVisibility();
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, loadOrders]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => (o.orderNumber ?? '').toLowerCase().includes(q));
  }, [orders, filter]);

  /* SSOO ISSUE 5 — split the customer's history so returns land in
     their own section. Active list still includes cancelled rows so
     the customer can see the outcome of a cancel they initiated. */
  const activeOrders = useMemo(
    () => filtered.filter((o) => stageOf(o) !== 'returned'),
    [filtered],
  );
  const returnedOrders = useMemo(
    () => filtered.filter((o) => stageOf(o) === 'returned'),
    [filtered],
  );

  // SSOO ISSUE 1 — customer-side cancel. Same shape the AccountOrders
  // page uses: send the INT enum (5), not the string label, because
  // the live PUT no-ops silently on the string form. Bug ref: AI Agent
  // 2026-06-11 — "new order cancelled from frontend does not update
  // status, still shows Delivered".
  const doCancel = useCallback(async () => {
    if (!cancelling) return;
    setCancelBusy(true);
    try {
      const next: SalesOrder = { ...cancelling, status: 5 };
      await salesOrderService.update([next]);
      toast.success('Order cancelled', cancelling.orderNumber ?? undefined);
      setCancelling(null);
      await loadOrders();
    } catch (e) {
      toast.error('Cancel failed', e instanceof Error ? e.message : 'Try again later.');
    } finally {
      setCancelBusy(false);
    }
  }, [cancelling, loadOrders, toast]);

  // SSOO ISSUE 5 — customer-side "Request Return". Any authenticated
  // user (customer role included) can POST /api/OrderReturn — the
  // controller is class-level [Authorize] with no role gate, TenantId
  // comes from the JWT, and the OrderReturn entity has no CustomerId
  // column so no ownership check is bypassed. The service cascades
  // SalesOrder.Status to 6 (Returned) after the return row commits,
  // which is why loadOrders() at the end moves the card into the
  // Returns section.
  const submitReturn = useCallback(async () => {
    if (!returnDraft) return;
    if (!returnDraft.reason) {
      setReturnError('Please pick a reason for the return.');
      return;
    }
    setReturnError(null);
    setReturnBusy(true);
    try {
      const o = returnDraft.order;
      /* Body mirrors what the panel's NewOrderReturn form sends. We
         send both `orderId` (the current wire field) and
         `salesOrderId` (legacy alias) so this keeps working if either
         controller is ahead of the other. `notes` prefixes the user's
         free-text with the picked refund method so the admin sees the
         customer's preference even though the entity has no
         dedicated `refundMethod` column. */
      const notesParts: string[] = [];
      if (returnDraft.refundMethod) {
        notesParts.push(`Preferred refund: ${returnDraft.refundMethod}`);
      }
      if (returnDraft.notes.trim()) notesParts.push(returnDraft.notes.trim());
      const body = {
        orderId: o.salesOrderId,
        salesOrderId: o.salesOrderId,
        orderType: 1,
        storeId: o.storeId,
        reason: returnDraft.reason,
        totalAmount: Number(o.totalAmount ?? 0),
        refundAmount: Number(o.totalAmount ?? 0),
        status: 'Requested',
        notes: notesParts.join('\n\n') || undefined,
        isActive: true,
      };
      await orderReturnService.create([body]);
      /* SSOO ISSUE 5 (ROUND-2) — success copy explicitly tells the
         customer where the order moved to, so they don't miss the
         Returns & refunds section that just appeared below. */
      toast.success(
        'Return request submitted',
        `Your order ${o.orderNumber ?? ''} is now in Returns & refunds.`,
      );
      setReturnDraft(null);
      await loadOrders();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Try again later.';
      setReturnError(msg);
      toast.error('Return request failed', msg);
    } finally {
      setReturnBusy(false);
    }
  }, [returnDraft, loadOrders, toast]);

  return (
    <div className="grid gap-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-[var(--color-primary)]" />
            <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Track your order
            </h1>
          </div>
          {/* Refresh + last-updated stamp. The page auto-refreshes
              every 60s while the tab is focused; this button forces
              an immediate re-fetch when the customer wants to verify
              "live" status right now. */}
          <div className="flex items-center gap-2">
            {refreshedAt && (
              <span className="text-[11px] text-content-muted">
                Updated {refreshedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              onClick={() => { void loadOrders(); }}
              disabled={loading}
              aria-label="Refresh order list"
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-surface-card px-2 text-xs font-medium text-content-secondary hover:bg-surface-card-hover hover:text-content-primary disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          Live status for every order on your account. Enter an order
          number to jump to it, or pick one from the list below.
        </p>
        <div className="mt-4 flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5">
          <Search className="h-4 w-4 text-[var(--color-text-muted)]" />
          <input
            type="text"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              const v = e.target.value.trim();
              const next = new URLSearchParams(params);
              if (v) next.set('orderNumber', v);
              else next.delete('orderNumber');
              setParams(next, { replace: true });
            }}
            placeholder="Order number (e.g. KSS-2026-04-1234)"
            className="min-w-0 flex-1 bg-transparent text-sm text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)]"
          />
        </div>
      </Card>

      {loading && orders.length === 0 ? (
        <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          Loading your orders…
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="grid place-items-center gap-3 p-10 text-center">
          <Package className="h-10 w-10 text-[var(--color-text-muted)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            {orders.length === 0
              ? 'No orders yet — once you place one, you can track it here.'
              : `No orders match "${filter}".`}
          </p>
          <Link
            to="/shop"
            className="inline-flex h-9 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
          >
            Browse the shop →
          </Link>
        </Card>
      ) : (
        <>
          <div className="grid gap-3">
            {activeOrders.map((o) => (
              <OrderTimelineCard
                key={String(o.salesOrderId ?? o.orderNumber)}
                order={o}
                loading={loading}
                onRefresh={() => { void loadOrders(); }}
                onCancel={() => setCancelling(o)}
                onRequestReturn={() =>
                  setReturnDraft({ order: o, reason: '', notes: '', refundMethod: '' })
                }
              />
            ))}
          </div>

          {/* SSOO ISSUE 5 (ROUND-2) — helper text when nobody in the
              active list is currently in a returnable state. Explains
              WHERE the return button will appear so the user stops
              hunting for it on orders that aren't eligible yet.
              Only shown when there's at least one active order but
              zero of them are shipped/delivered, so we don't lecture
              customers who obviously already know. */}
          {activeOrders.length > 0 &&
            !activeOrders.some((o) => isReturnable(stageOf(o))) && (
              <Card className="p-3">
                <div className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]" />
                  <span>
                    You'll be able to <b>return items</b> once they arrive.
                    While an order is still being prepared you can{' '}
                    <b>cancel</b> it from the button on the order card.
                  </span>
                </div>
              </Card>
            )}

          {/* SSOO ISSUE 5 — dedicated Returns & refunds section for
              orders whose status the backend flipped to Returned=6
              after a return was created. Only rendered when there's
              at least one so the heading doesn't create visual noise
              for customers with no returns. */}
          {returnedOrders.length > 0 && (
            <>
              <div className="mt-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
                  Returns &amp; refunds
                </h2>
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {returnedOrders.length} {returnedOrders.length === 1 ? 'order' : 'orders'}
                </p>
              </div>
              <div className="grid gap-3">
                {returnedOrders.map((o) => (
                  <OrderTimelineCard
                    key={String(o.salesOrderId ?? o.orderNumber)}
                    order={o}
                    loading={loading}
                    onRefresh={() => { void loadOrders(); }}
                    onCancel={() => setCancelling(o)}
                    onRequestReturn={() =>
                      setReturnDraft({ order: o, reason: '', notes: '', refundMethod: '' })
                    }
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* SSOO ISSUE 1 — cancel confirmation. Reuses the shared
          ConfirmDialog so the copy matches My Orders' cancel flow. */}
      <ConfirmDialog
        isOpen={!!cancelling}
        title="Cancel this order?"
        message={`Order ${cancelling?.orderNumber ?? ''} will be cancelled and refunded if already paid.`}
        confirmLabel={cancelBusy ? 'Cancelling…' : 'Cancel order'}
        cancelLabel="Keep order"
        onConfirm={doCancel}
        onClose={() => setCancelling(null)}
        variant="danger"
      />

      {/* SSOO ISSUE 5 — Request Return modal. Title adapts to whether
          the customer clicked "Report an issue" (shipped) or "Request
          return" (delivered) so the modal header matches the button
          they just pressed. */}
      <Modal
        open={!!returnDraft}
        onClose={() => {
          if (returnBusy) return;
          setReturnDraft(null);
          setReturnError(null);
        }}
        title={`${
          returnDraft ? returnActionLabel(stageOf(returnDraft.order)) : 'Request return'
        } · ${returnDraft?.order.orderNumber ?? ''}`}
        width={520}
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setReturnDraft(null);
                setReturnError(null);
              }}
              disabled={returnBusy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => { void submitReturn(); }}
              /* SSOO ISSUE 5 (ROUND-2) — submit stays disabled while
                 the request is in flight AND when no reason is picked
                 so accidental double-taps can't fire two return rows
                 for the same order. */
              disabled={returnBusy || !returnDraft?.reason}
              aria-busy={returnBusy}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {returnBusy ? 'Submitting…' : 'Submit request'}
            </Button>
          </>
        }
      >
        {returnDraft && (
          <div className="grid gap-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Tell us why you'd like to return this order. Our support
              team will review your request and get back to you shortly.
            </p>
            <SelectInput
              label="Reason"
              name="return-reason"
              required
              placeholder="Pick a reason…"
              options={RETURN_REASONS}
              value={returnDraft.reason}
              onChange={(e) =>
                setReturnDraft((p) => (p ? { ...p, reason: e.target.value } : p))
              }
            />
            <TextareaInput
              label="Notes (optional)"
              name="return-notes"
              placeholder="Any additional details that will help us process this return."
              value={returnDraft.notes}
              onChange={(e) =>
                setReturnDraft((p) => (p ? { ...p, notes: e.target.value } : p))
              }
              rows={3}
            />
            <SelectInput
              label="Preferred refund method (optional)"
              name="refund-method"
              options={REFUND_METHODS}
              value={returnDraft.refundMethod}
              onChange={(e) =>
                setReturnDraft((p) => (p ? { ...p, refundMethod: e.target.value } : p))
              }
            />
            {returnError && <Alert variant="danger">{returnError}</Alert>}
          </div>
        )}
      </Modal>
    </div>
  );
}

interface OrderTimelineCardProps {
  order: SalesOrder;
  loading: boolean;
  onRefresh: () => void;
  onCancel: () => void;
  onRequestReturn: () => void;
}

function OrderTimelineCard({
  order,
  loading,
  onRefresh,
  onCancel,
  onRequestReturn,
}: OrderTimelineCardProps) {
  const id = String(order.salesOrderId ?? '');
  const stage = stageOf(order);
  const date = (order.orderDate ?? order.createdAt ?? '').slice(0, 10);
  const total = Number(order.totalAmount ?? 0);
  // SSOO ISSUE 1 — surface stage-specific timestamps under each pip
  // so "the status isn't updating" complaints become verifiable: the
  // shipped date is stamped as soon as the panel marks the order
  // Shipped=3, and updatedAt captures the final Delivered=4 flip.
  const shippedStamp = fmtStamp(order.shippedAt ?? null);
  const deliveredStamp = stage === 'delivered' || stage === 'returned' ? fmtStamp(order.updatedAt) : '';
  /* SSOO ISSUE 1 (ROUND-2) — expose "Last updated: N min ago" from
     `order.updatedAt` so the shopper can tell at a glance whether the
     tracker view reflects the current backend state. Complements the
     header refresh timestamp (which is when WE last fetched) with an
     order-specific stamp (when the backend last touched the row). */
  const lastUpdatedRel = fmtRelative(order.updatedAt ?? null);
  const currentLabel = stageDisplayLabel(stage);

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] px-5 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {order.orderNumber ?? id}
            </div>
            {/* SSOO ISSUE 1 (ROUND-2) — prominent "Current: X" badge so
                the shopper doesn't have to visually decode the pipeline
                to know where the order stands right now. Reads the
                same stage the timeline paints, so the two can't drift. */}
            <StatusBadge status={currentLabel} />
          </div>
          <div className="text-[11px] text-[var(--color-text-muted)]">
            Placed on {date || '—'} · ₹ {total.toLocaleString('en-IN')}
            {lastUpdatedRel && (
              <>
                {' '}· <span title={fmtStamp(order.updatedAt)}>Last updated {lastUpdatedRel}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* SSOO ISSUE 1 (ROUND-2) — per-card Refresh so a shopper
              watching one specific order can pull fresh status without
              waiting for the 60-second poll or hunting for the header
              button. All cards share the same fetch call so this
              refreshes the whole list, but the button lives next to
              the row the customer cares about. */}
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh status"
            title="Refresh status"
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-surface-card px-2 text-[11px] font-medium text-content-secondary hover:bg-surface-card-hover hover:text-content-primary disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh status
          </button>
          <Link
            to={`/account/orders/${id}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
          >
            View details <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Timeline — 4 horizontal steps for the happy path. Cancelled
          and Returned orders show a single inline stamp instead of the
          full pipeline. */}
      <div className="px-5 py-5">
        {stage === 'cancelled' ? (
          <div className="flex items-center gap-2 rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger-light)] p-3 text-sm text-[var(--color-danger)]">
            <XCircle className="h-4 w-4" />
            <span className="font-semibold">Order cancelled.</span>
            <span className="text-xs">Need help? Contact support.</span>
          </div>
        ) : stage === 'returned' ? (
          <div className="flex items-center gap-2 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning-light)] p-3 text-sm text-[var(--color-warning)]">
            <RotateCcw className="h-4 w-4" />
            <span className="font-semibold">Return in progress.</span>
            <span className="text-xs">
              We'll update you once the refund is processed.
            </span>
          </div>
        ) : (
          <ol className="flex items-center justify-between gap-2">
            {STAGE_ORDER.map((s, i) => {
              const Icon = STAGE_ICON[s];
              const done = isPastStage(stage, s);
              const current = stage === s;
              const stamp =
                s === 'shipped' && (done || current) ? shippedStamp
                : s === 'delivered' && current ? deliveredStamp
                : '';
              return (
                <li key={s} className="flex flex-1 items-center gap-2">
                  <div className="flex flex-col items-center gap-1">
                    <span
                      className={[
                        'inline-flex h-9 w-9 items-center justify-center rounded-full border-2',
                        done
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
                          : current
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                            : 'border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-muted)]',
                      ].join(' ')}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span
                      className={[
                        'whitespace-nowrap text-[11px] font-medium',
                        done || current
                          ? 'text-[var(--color-text-primary)]'
                          : 'text-[var(--color-text-muted)]',
                      ].join(' ')}
                    >
                      {STAGE_LABEL[s]}
                    </span>
                    {stamp && (
                      <span className="whitespace-nowrap text-[10px] text-[var(--color-text-muted)]">
                        {stamp}
                      </span>
                    )}
                  </div>
                  {i < STAGE_ORDER.length - 1 && (
                    <span
                      className={[
                        'h-0.5 flex-1 rounded-full',
                        done
                          ? 'bg-[var(--color-primary)]'
                          : 'bg-[var(--color-border)]',
                      ].join(' ')}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* SSOO ISSUE 1 / ISSUE 5 — customer-callable actions row.
            Cancel appears on pre-shipped orders; Request Return
            appears on shipped/delivered orders. The button is a
            *primary* variant so it doesn't disappear into the border
            noise the way an outline pill did — user complaint was
            "still no Order Returns option visible" and the fix is to
            make it unmissable. Kept below the timeline so the visual
            hierarchy stays: status first, actions second. */}
        {(isCancellable(stage) || isReturnable(stage)) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {isCancellable(stage) && (
              <Button size="sm" variant="outline" onClick={onCancel}>
                <XCircle className="h-3.5 w-3.5" />
                Cancel order
              </Button>
            )}
            {isReturnable(stage) && (
              <Button size="sm" variant="primary" onClick={onRequestReturn}>
                {stage === 'shipped' ? (
                  <AlertTriangle className="h-3.5 w-3.5" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                {returnActionLabel(stage)}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
